"use client";

import { useModal } from "@/contexts/ModalContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { normalizeTurkish } from "@/utils/stringUtils";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "../Modal";

// ============================================================================
// SELECT OPTION COMPONENT
// ============================================================================
const CustomSelectOption = memo(
  ({ option, isSelected, onSelect, renderOption, labelKey }) => {
    return (
      <button
        type="button"
        onClick={() => onSelect(option)}
        className={clsx(
          "hover:bg-background-global border-border flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg p-4 text-left transition-colors duration-150 md:mb-0 md:rounded-none md:border-0 md:border-b-0 md:px-3.5 md:py-2.5",
          isSelected && "bg-background-global font-medium",
        )}
      >
        <div className="flex flex-1 items-center gap-2">
          {renderOption ? (
            renderOption(option)
          ) : (
            <span>{option[labelKey]}</span>
          )}
        </div>
        {isSelected && (
          <FontAwesomeIcon
            icon={byPrefixAndName.far["check"]}
            className="text-teal text-sm"
          />
        )}
      </button>
    );
  },
);

CustomSelectOption.displayName = "CustomSelectOption";

// ============================================================================
// SELECT COMPONENT
// ============================================================================
const CustomSelect = forwardRef(
  (
    {
      // Data props
      options = [],
      value,
      onChange,

      // Key configuration
      valueKey = "value",
      labelKey = "label",

      // Display props
      label,
      placeholder = "Seçiniz",
      renderOption, // Custom option renderer - dropdown'da kullanılır
      renderSelected, // OPTIONAL - button'da kullanılır, yoksa renderOption kullanılır

      // Search configuration
      searchable = true,
      autoShowSearch, // undefined = auto (>5 items), true/false = force
      searchKeys, // Array of keys to search in, defaults to [labelKey, valueKey]

      // Placeholder option
      withPlaceholderOption = false,
      placeholderOptionText,
      placeholderOptionValue = "",

      // Validation
      error,
      required = false,

      // State
      disabled = false,

      // Styling
      className = "",
      buttonClassName = "",
      dropdownClassName = "",
      optionClassName = "",
      optionListClassName = "",
      selectedOptionClassname = "",
      buttonIconClassName = "",
      mobileDropdownClassName = "",
      // HTML attributes
      id,
      name,

      // Events
      onFocus,
      onBlur,

      ...props
    },
    ref,
  ) => {
    const t = useTranslationDetector();
    const isMobile = useIsMobile();

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const selectId = useMemo(
      () => id || name || `select-${Math.random().toString(36).substr(2, 9)}`,
      [id, name],
    );
    const modalId = useMemo(() => `select-modal-${selectId}`, [selectId]);

    const modal = useModal(modalId);

    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const searchInputRef = useRef(null);
    const hiddenInputRef = useRef(null);

    // Expose ref methods
    useImperativeHandle(ref, () => ({
      focus: () => buttonRef.current?.focus(),
      blur: () => buttonRef.current?.blur(),
      getValue: () => value,
      setValue: (newValue) => {
        const syntheticEvent = {
          target: { value: newValue, name: name },
          currentTarget: { value: newValue, name: name },
        };
        onChange?.(syntheticEvent);
      },
      open: () => handleToggle(),
      close: () => {
        setIsOpen(false);
        setSearchTerm("");
        if (isMobile) {
          modal.close();
        }
      },
    }));

    // Determine if search should be shown
    const shouldShowSearch = useMemo(() => {
      if (autoShowSearch !== undefined) return autoShowSearch;
      return searchable && options.length > 5;
    }, [autoShowSearch, searchable, options.length]);

    // Prepare options with placeholder
    const allOptions = useMemo(() => {
      if (!withPlaceholderOption) return options;

      const placeholderOption = {
        [valueKey]: placeholderOptionValue,
        [labelKey]: placeholderOptionText || placeholder || t("common.select"),
        isPlaceholder: true,
      };

      return [placeholderOption, ...options];
    }, [
      withPlaceholderOption,
      options,
      valueKey,
      labelKey,
      placeholderOptionValue,
      placeholderOptionText,
      placeholder,
      t,
    ]);

    // Find selected option
    const selectedOption = useMemo(
      () => allOptions.find((opt) => opt[valueKey] === value),
      [allOptions, value, valueKey],
    );

    // Search keys configuration
    const effectiveSearchKeys = useMemo(
      () => searchKeys || [labelKey, valueKey],
      [searchKeys, labelKey, valueKey],
    );

    // Filter options based on search
    const filteredOptions = useMemo(() => {
      if (!searchTerm || !shouldShowSearch) return allOptions;

      const normalizedSearchTerm = normalizeTurkish(searchTerm);

      return allOptions.filter((opt) => {
        return effectiveSearchKeys.some((key) => {
          const value = opt[key];
          if (!value) return false;

          const normalizedValue = normalizeTurkish(String(value));
          return normalizedValue.includes(normalizedSearchTerm);
        });
      });
    }, [allOptions, searchTerm, shouldShowSearch, effectiveSearchKeys]);

    // Handle click outside for desktop
    useEffect(() => {
      if (!isMobile && isOpen) {
        const handleClickOutside = (event) => {
          if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
          ) {
            setIsOpen(false);
            setSearchTerm("");
          }
        };

        document.addEventListener("mousedown", handleClickOutside);

        // Auto-focus search input
        if (shouldShowSearch) {
          setTimeout(() => searchInputRef.current?.focus(), 50);
        }

        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }
    }, [isOpen, isMobile, shouldShowSearch]);

    // Handle option selection
    const handleSelect = useCallback(
      (option) => {
        const selectedValue = option[valueKey];

        const syntheticEvent = {
          target: { value: selectedValue, name: name },
          currentTarget: { value: selectedValue, name: name },
        };

        onChange?.(syntheticEvent);

        setIsOpen(false);
        setSearchTerm("");

        if (isMobile) {
          modal.close();
        }

        // Update hidden input
        if (hiddenInputRef.current) {
          hiddenInputRef.current.value = selectedValue;
        }
      },
      [onChange, name, isMobile, modal, valueKey],
    );

    // Handle toggle
    const handleToggle = useCallback(() => {
      if (disabled) return;

      if (isMobile) {
        modal.open({
          isFullScreen: false,
          isBottomSheet: true,
          useSafeViewport: true,
          headerClassName: "!border-0",
          contentClassName:
            "!px-4 !pt-0 !pb-[calc(env(safe-area-inset-bottom,0px)+16px)] flex-1 overflow-y-auto p-6 md:px-6 md:pt-4 md:pb-5",
          title: label || t("common.select"),
        });
      } else {
        setIsOpen((prev) => !prev);
      }
    }, [disabled, isMobile, modal, label, t]);

    // Handle search change
    const handleSearchChange = useCallback((e) => {
      setSearchTerm(e.target.value);
    }, []);

    // 🎯 BUTTON CONTENT RENDERER
    // renderSelected yoksa renderOption kullanılır
    const renderButtonContent = useMemo(() => {
      if (!selectedOption) {
        return <span className="text-gray-400">{placeholder}</span>;
      }

      if (selectedOption.isPlaceholder) {
        return (
          <span className="text-gray-400">{selectedOption[labelKey]}</span>
        );
      }

      // 1. renderSelected varsa onu kullan
      if (renderSelected) {
        return (
          <div
            className={clsx(
              "flex flex-1 items-center gap-2",
              selectedOptionClassname,
            )}
          >
            {renderSelected(selectedOption)}
          </div>
        );
      }

      // 2. renderSelected yoksa renderOption'ı kullan
      if (renderOption) {
        return (
          <div
            className={clsx(
              "flex flex-1 items-center gap-2",
              selectedOptionClassname,
            )}
          >
            {renderOption(selectedOption)}
          </div>
        );
      }

      // 3. İkisi de yoksa sadece label
      return (
        <span className={clsx("text-gray-900", selectedOptionClassname)}>
          {selectedOption[labelKey]}
        </span>
      );
    }, [selectedOption, renderSelected, renderOption, labelKey, placeholder]);

    // Render dropdown content
    const renderDropdownContent = useMemo(
      () => (
        <>
          {/* Search Input */}
          {shouldShowSearch && (
            <div className="sticky top-0 z-10 bg-white p-0 md:p-2">
              <div className="relative">
                <span className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-500 md:hidden">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.fal["magnifying-glass"]}
                  />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t("common.search")}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="border-primary-weak focus:border-primary w-full rounded-md border-2 p-4 pl-11 outline-none focus:ring-0 md:px-3 md:py-2 md:pl-4"
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      searchInputRef.current?.focus();
                    }}
                    className="bg-background-400 text-foreground-muted absolute top-1/2 right-4 flex h-6 w-6 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full md:hidden"
                  >
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["xmark"]}
                      className="text-sm"
                    />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Options List */}
          <div
            className={clsx(
              "scrollbar-default overflow-y-auto pr-0.5 md:pr-0",
              shouldShowSearch
                ? "mt-4 h-[calc(100%-76px)] min-h-[500px] md:m-0 md:max-h-64 md:min-h-auto"
                : "min-h-[500px] md:max-h-80 md:min-h-auto",
              optionListClassName,
            )}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <CustomSelectOption
                  key={option[valueKey]}
                  option={option}
                  isSelected={option[valueKey] === value}
                  onSelect={handleSelect}
                  renderOption={renderOption}
                  labelKey={labelKey}
                />
              ))
            ) : (
              <div className="px-3.5 py-6 text-center text-gray-500">
                {t("common.noResults")}
              </div>
            )}
          </div>
        </>
      ),
      [
        shouldShowSearch,
        searchTerm,
        handleSearchChange,
        filteredOptions,
        value,
        handleSelect,
        renderOption,
        labelKey,
        valueKey,
        t,
      ],
    );

    return (
      <div className={clsx("relative flex flex-col", className)}>
        <div ref={dropdownRef} className="relative">
          {/* Label */}
          {label && (
            <label
              htmlFor={selectId}
              className={clsx(
                "pointer-events-none absolute left-4 flex items-center gap-1 transition-all duration-200",
                // Select'te label her zaman floating
                "-top-2 px-2 text-xs",
                !disabled && "bg-white",
                error && "text-danger",
                disabled && "text-gray-400",
              )}
            >
              {label}
            </label>
          )}
          {/* Select Button */}
          <button
            ref={buttonRef}
            type="button"
            id={selectId}
            onClick={handleToggle}
            disabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            className={clsx(
              "cursor-pointe focus:ring-primary flex h-[56px] w-full items-center justify-between gap-2 rounded-md border px-3.5 py-2.5 text-left text-base font-medium transition-all duration-200 outline-none focus:ring-2",
              error
                ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                : "border-border focus:border-primary",
              disabled &&
                "cursor-not-allowed bg-gray-100 text-gray-400 opacity-60",
              buttonClassName,
            )}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-labelledby={label ? `${selectId}-label` : undefined}
          >
            {renderButtonContent}

            {!disabled && (
              <FontAwesomeIcon
                icon={byPrefixAndName.fal["angle-down"]}
                className={clsx(
                  "text-foreground ml-2 flex-shrink-0 transition-transform duration-200",
                  isOpen && "rotate-180",
                  buttonIconClassName,
                )}
              />
            )}
          </button>

          {/* Hidden Input for Form Submission */}
          <input
            ref={hiddenInputRef}
            type="hidden"
            name={name}
            value={value || ""}
            readOnly
          />

          {/* Desktop: Dropdown */}
          {!isMobile && isOpen && (
            <div
              className={clsx(
                "shadow-xxl absolute top-full left-0 z-50 mt-2 w-full overflow-hidden rounded-md bg-white",
                dropdownClassName,
              )}
            >
              {renderDropdownContent}
            </div>
          )}

          {/* Mobile: Modal */}
          {isMobile && (
            <Modal className={clsx(mobileDropdownClassName)} id={modalId}>
              {renderDropdownContent}
            </Modal>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p
            className="text-danger mt-1.5 flex items-center gap-1 pl-4 text-xs"
            id={`${selectId}-error`}
          >
            <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
            <span>
              {typeof error === "string" ? error : t("common.required")}
            </span>
          </p>
        )}
      </div>
    );
  },
);

CustomSelect.displayName = "CustomSelect";

const MemoizedCustomSelect = memo(CustomSelect);
MemoizedCustomSelect.displayName = "CustomSelect";

export default CustomSelect;
